import { useMemo } from "react";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import KPIGrid from "@/components/dashboard/KPIGrid";
import ProjectPerformanceChart from "@/components/dashboard/charts/ProjectPerformanceChart";
import useDashboardData from "@/hooks/useDashboardData";
import { Card, CardContent } from "@/components/ui/card";

const formatCount = (value) =>
  new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 0,
  }).format(value || 0);

export default function DashboardProjects() {
  const forceHistoricalRange = true;
  const {
    overview,
    overviewLoading,
    overviewError,
    topProjects,
    comparisonLabel,
    comparisonRangeLabel,
  } = useDashboardData({
    includeForecast: false,
    includeTimeseries: false,
    includeProjects: false,
    includeStatus: false,
    forceHistorical: true,
  });

  const kpiItems = useMemo(() => {
    if (!overview) return [];
    const projects = topProjects || [];
    const topRevenue = projects[0]?.revenue || 0;
    const avgRevenue = projects.length
      ? projects.reduce((sum, project) => sum + (project.revenue || 0), 0) / projects.length
      : 0;
    return [
      {
        key: "top-project-revenue",
        title: "Top Projekt Umsatz",
        value: topRevenue,
        testId: "kpi-top-project-revenue",
      },
      {
        key: "avg-project-revenue",
        title: "Ø Umsatz (Top)",
        value: avgRevenue,
        testId: "kpi-avg-project-revenue",
      },
      {
        key: "project-count",
        title: "Aktive Projekte",
        value: projects.length,
        valueFormatter: formatCount,
        testId: "kpi-project-count",
      },
      {
        key: "net",
        title: "Netto Ergebnis",
        value: overview.net?.current || 0,
        changePercent: overview.net?.change_percent,
        comparisonValue: overview.net?.previous,
        comparisonLabel,
        comparisonRangeLabel,
        changeLabel: `ggü. ${comparisonLabel}`,
        testId: "kpi-net",
      },
    ];
  }, [overview, topProjects, comparisonLabel, comparisonRangeLabel]);

  const showHistorical = true;

  return (
    <div className="space-y-6" data-testid="dashboard-projects-view">
      <DashboardFilters
        title="Projekt-Filter"
        subtitle="Fokussiere Projekte nach Kunden & Zeitraum"
        showProjects={false}
        showCustomers
      />

      <section data-testid="dashboard-projects-kpis">
        {!showHistorical && (
          <Card>
            <CardContent className="p-6 text-sm text-slate-600" data-testid="dashboard-no-data">
              Für reine Zukunftszeiträume stehen keine historischen Projektdaten zur Verfügung.
            </CardContent>
          </Card>
        )}
        {overviewLoading && <KPIGrid isLoading />}
        {!overviewLoading && overviewError && (
          <Card>
            <CardContent className="p-6 text-sm text-red-600" data-testid="kpi-error">
              KPI-Daten konnten nicht geladen werden.
            </CardContent>
          </Card>
        )}
        {showHistorical && !overviewLoading && !overviewError && kpiItems.length > 0 && (
          <KPIGrid items={kpiItems} />
        )}
      </section>

      {showHistorical && (
        <section data-testid="dashboard-projects-charts">
          <ProjectPerformanceChart data={topProjects} isLoading={overviewLoading} error={overviewError} />
        </section>
      )}
    </div>
  );
}